import { Injectable } from '@angular/core';
import { AsgardTab } from 'src/app/core/asgard-tab';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { PaneService } from './pane.service';
import { TranslateService } from '@ngx-translate/core';
import { DediconPDFService } from './dedicon-pdf.service';
import { MimeTypesEnum } from '../utils/enums';
import { MatDialog } from '@angular/material/dialog';
import { ModalWindowService } from './ui/modal-window.service';
import { RemoveExtensionStringPipe } from '../utils/pipes/remove-extension-string.pipe';
import { DocumentOpenedDialogComponent } from '../components/dialogs/document-opened-dialog/document-opened-dialog.component';
import { ReturnMimetypePipe } from '../utils/pipes/return-mimetype.pipe';
import { getMimeTypeFromFile } from '../core/common';
import { MessageDialogActionColor, MessageDialogComponent, MessageDialogData } from '../components/dialogs/message-dialog/message-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class TabService {
  private readonly tabLangUpdate: Subject<string> = new Subject();
  private readonly currentTabsSubject: BehaviorSubject<AsgardTab[]> = new BehaviorSubject<AsgardTab[]>([]);
  private readonly activeTabSub: BehaviorSubject<AsgardTab | null> = new BehaviorSubject(null);
  private readonly closeTabSub: BehaviorSubject<string | null> = new BehaviorSubject(null);
  private readonly beforeTabChangedSub: BehaviorSubject<string | null> = new BehaviorSubject(null);

  private currentTabs: AsgardTab[] = [];
  private activePaneIndex: number = 0;
  private closeAnyway: boolean = false;

  get activeTab(): AsgardTab | null {
    const tab = this.currentTabs.find(i => i.isActive && i.paneIndex === this.activePaneIndex);
    if (!tab) {
      return null;
    }
    return tab;
  }

  get allTabs(): AsgardTab[] {
    return this.currentTabs;
  }

  constructor(
    private paneService: PaneService,
    private translate: TranslateService,
    private dediconPDFService: DediconPDFService,
    private modalService: ModalWindowService,
    private removeExtensionPipe: RemoveExtensionStringPipe,
    private returnMimetypePipe: ReturnMimetypePipe,
    private readonly dialog: MatDialog
  ) {
    this.subToActivePaneIndex();
  }

  getActiveTabByPaneIndex(paneIndex: number | null): AsgardTab | null{
    if (paneIndex === null)
      return this.activeTab;
    
    const tab = this.currentTabs.find(i => i.isActive && i.paneIndex === paneIndex);

    return !tab ? null : tab;
  }

  activeTabObservable(): Observable<AsgardTab | null> {
    return this.activeTabSub.asObservable();
  }

  closeTabObservable(): Observable<string | null> {
    return this.closeTabSub.asObservable();
  }

  beforeTabChanged(): Observable<string | null> {
    return this.beforeTabChangedSub.asObservable();
  }

  getCurrentTabs(): Observable<AsgardTab[]> {
    return this.currentTabsSubject.asObservable();
  }

  onTabLangUpdate(): Observable<string> {
    return this.tabLangUpdate.asObservable();
  }

  selectTab(id: string): void {
    this.triggerBeforeTabChanged();

    this.currentTabs.forEach(i => {
      if (i.id === id && this.activePaneIndex === i.paneIndex) {
        i.setActive(id);
      } else if (this.activePaneIndex === i.paneIndex) {
        i.setActive(id);
      }
    });
    this.updateTabsSubject();
  }

  async closeTab(id: string): Promise<void> {
    const closedTab = this.currentTabs.find(t => t.id === id);

    if (!!closedTab) {
      this.triggerBeforeTabChanged();
      this.closeTabSub.next(closedTab.id);
      this.currentTabs = this.currentTabs.filter(t => t !== closedTab);
    }

    if (this.currentTabs.length) {
      this.currentTabs[this.currentTabs.length - 1].isActive = true;
    }

    this.updateTabsSubject();
  }

  public async prepareCloseTab(unsavedchanges: boolean = true): Promise<boolean> {
    this.closeAnyway = true;

    if (unsavedchanges) {
      await this.showCloseWarning();
    }
    return this.closeAnyway;
  }

  private async showCloseWarning(): Promise<void> {
    return new Promise<void>((resolve) => {
      const data: MessageDialogData = {
        title: 'DIALOG.CLOSETAB.TITLE',
        message: 'DIALOG.CLOSETAB.MESSAGE',
        actions: [
          {
            title: 'DIALOG.CLOSETAB.CONFIRM',
            color: MessageDialogActionColor.Primary,
            handler: () => {this.closeAnyway = true; resolve()}
          },
          {
            title: 'DIALOG.CLOSETAB.CANCEL',
            color: MessageDialogActionColor.None,
            handler: () => {this.closeAnyway = false; resolve()}
          }
        ]
      };
      this.dialog.open(MessageDialogComponent, { data });
    });
  }

  closeAllTabs(): void {
    this.currentTabs.splice(0, this.currentTabs.length);
    this.setActiveTab();
  }

  addNewTab(tabTitle: string | undefined = undefined, createEditor: boolean = true, paneIndex: number | null = null): AsgardTab {

    this.triggerBeforeTabChanged();
    // console.warn('addNewTab paneIndex', paneIndex);
    const tabData = new AsgardTab((paneIndex || paneIndex === 0) ? paneIndex : this.activePaneIndex, tabTitle, createEditor);
    this.currentTabs.forEach(i => {
      if (i.paneIndex === tabData.paneIndex) {
        i.isActive = false;
      }
    });
    // console.log(tabData, 'tabData');
    this.currentTabs.push(tabData);
    // console.log('this.currentTabs: ', this.currentTabs);
    this.updateTabsSubject();
    return tabData;
  }

  setActiveTabName(tabName: string): void {
    const activeTab = this.activeTab;

    if (activeTab) {
      activeTab.setTabTitle(tabName);
      this.updateTabsSubject(false);
    }
  }

  getTabById(tabId: string): AsgardTab | null {
    for (const tab of this.currentTabs) {
      if (tab.id === tabId) {
        return tab;
      }
    }

    return null;
  }

  getTabsByFileName(tabFileName: string): AsgardTab[] {
    return this.currentTabs.filter(i => i.originalFileName === tabFileName);
  }

  setTabLanguage(tabId: string, lang: string, voice: string): void {
    const targetTab = this.getTabById(tabId);

    if (targetTab !== null && lang !== null && voice !== null) {
      targetTab.tabStore.currLangCode = lang;
      targetTab.tabStore.currVoiceName = voice;
      this.tabLangUpdate.next(tabId);
    }
  }

  setTabReadingSpeed(tabId: string, speed: number) {
    const targetTab = this.getTabById(tabId);

    if (targetTab !== null) {
      targetTab.tabStore.currReadingSpeed = speed;
    }
  }

  getTabLanguage(tabId: string): string | null {
    const targetTab = this.getTabById(tabId);
    return targetTab ? targetTab.tabStore.currLangCode : null;
  }

  getTabVoice(tabId: string): string | null {
    const targetTab = this.getTabById(tabId);
    return targetTab ? targetTab.tabStore.currVoiceName : null;
  }

  getTabReadingSpeed(tabId: string): number | null {
    const targetTab = this.getTabById(tabId);
    return targetTab ? targetTab.tabStore.currReadingSpeed : null;
  }

  async setTitleForActiveTab(title: string): Promise<void> {
    if (this.activeTab === null) {
      return;
    }

    try {
      const validTitle = await this.validateTitle(title, this.activeTab);
      this.activeTab.setTabTitle(validTitle);
    } catch {
      this.closeTab(this.activeTab.id);
    }
  }

  setActiveTabAsSTISource(): void {
    if (this.activeTab === null) {
      console.error('No active tab to set as STI source.');
      return;
    }

    this.activeTab.isSTIFile = true;
  }

  async setTabFile(file: File, originalFileName: string | null = null, paneIndex: number | null = null): Promise<string> {
    // console.log('setTabFile Service: ', paneIndex);
    try {
      let password = null;
      let isKES = false;
      let isDediconFile = false;
      let isFileWithoutExtension = false;
      // if pdf file without extension, recreate file.
      if (originalFileName === undefined && file.type === MimeTypesEnum.ApplicationPdf) {
        if (file.name.indexOf('.pdf') < 0) {
          file = new File([file], file.name + '.pdf', { type: file.type });
        }
      }

      let originalName = originalFileName ? originalFileName : file.name;

      if (file.type === MimeTypesEnum.ApplicationPdf && file.name.toLowerCase().indexOf('.pdf') === -1) {
        isFileWithoutExtension = true;
        originalName = file.name + '.pdf'; // add .pdf if needed or the security handler will go bananas if dedicon pdf
      }

      if (file.name.match(/(\.pdf)$/i)) {
        if (originalName && originalName.match(/(\.sek|\.kes)$/i)) {
          isKES = true;
        }
        originalName = file.name;
        const fileWithDetails = await this.dediconPDFService.tryGetDetailsFromDediconFile(file);
        this.dediconPDFService.closeDediconToastr(file.name);
        password = fileWithDetails.password;
        file = fileWithDetails.file;
        isDediconFile = fileWithDetails.isDediconFile || false;
        // file = file;
        // isDediconFile = false;
      }

      if (isFileWithoutExtension) {
        file = new File([file], file.name.replace('.pdf', ''), { type: file.type });
        originalName = file.name;
      }

      if (file) {
        const activeTab = this.addNewTab(originalName, false, paneIndex);
        // console.log('activeTab', activeTab);
        activeTab.isKES = isKES;

        activeTab.setTabFile(file, originalName, password, isDediconFile);
        return Promise.resolve(activeTab.id);
      }

      return Promise.resolve('');
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private subToActivePaneIndex(): void {
    this.paneService.getActivePaneObservable()
      .subscribe(
        (e: 0 | 1) => {
          if ((e || e === 0) && e !== this.activePaneIndex) {
            this.activePaneIndex = e;
            this.setActiveTab();
          }
        }
      );
    this.paneService.ChangePandeIndexForTabsEv
      .subscribe((e: boolean) => {
        if (e) {
          this.moveTabsFromClosedPane();
        }
      });
  }

  private updateTabsSubject(updateActive: boolean = true): void {
    if (updateActive) {
      this.setActiveTab();
    }
    this.currentTabsSubject.next(this.currentTabs);
  }

  private moveTabsFromClosedPane(): void {
    this.triggerBeforeTabChanged();

    const lastSelectedTab = this.activeTab;

    this.currentTabs.forEach(i => {
      i.unSelectTab();
    });
    this.currentTabs.forEach(i => {
      if (i.paneIndex === 1) {
        i.paneIndex = 0;
        if (lastSelectedTab && lastSelectedTab.id === i.id) {
          i.setActive(i.id);
        }
      }
    });
    const selected = this.currentTabs.find(i => i.isActive);
    if (!selected) {
      if (this.currentTabs.length) {
        this.currentTabs[this.currentTabs.length - 1].isActive = true;
      }
    }
    this.setActiveTab();
    this.updateTabsSubject();
    this.paneService.destroyPane();
  }

  private setActiveTab(): void {
    const tab = this.activeTab;
    const activeTab = this.activeTabSub.getValue();
    if (tab && activeTab === null || tab && activeTab && activeTab.id !== tab.id) {
      this.activeTabSub.next(tab);
    } else if (tab === null) {
      this.activeTabSub.next(null);
    }
  }

  private triggerBeforeTabChanged(): void {
    const tab = this.activeTab;

    if (tab) {
      this.beforeTabChangedSub.next(tab.id);
    }
  }

  public validateTitle(title: string, tab: AsgardTab): Promise<string> {
    const openCopies = this.getTabsByFileName(title).filter(copyTab => copyTab.id !== tab.id);
    if (openCopies.length) {
      return new Promise<string>((resolve, reject) => {
        this.modalService.createModalWindow(DocumentOpenedDialogComponent);
        const subscription = this.modalService.closingModal.subscribe((result: boolean) => {
          if (result) {
            resolve(this.addCopyToTitle(title, openCopies.length));
          } else {
            reject(new Error('Invalid tab title'));
          }
          subscription.unsubscribe();
        });
      });
    } else {
      return Promise.resolve(title);
    }
  }

  private addCopyToTitle(fileName: string, copyNumber: number): string {
    const fileNameWithoutExtension = this.removeExtensionPipe.transform(fileName);
    const fileExtension = this.returnMimetypePipe.transform(getMimeTypeFromFile(fileName));
    return `${fileNameWithoutExtension} (${this.translate.instant('COMMON.COPY')} ${copyNumber})${fileExtension}`;
  }

}
